import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import ConceptTeachCard from '@/components/ConceptTeachCard';
import PracticeExitControls from '@/components/PracticeExitControls';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Loader2, Check, X, Trophy, RotateCcw, Home } from 'lucide-react';
import { toast } from 'sonner';

const gradeFromChild = (child) => {
  if (!child) return 'K';
  const explicit = (child.grade_level || '').toString().trim().toUpperCase();
  if (explicit) {
    if (['PK', 'PRE-K', 'PREK'].includes(explicit)) return 'PK';
    if (['K', 'KG', 'KINDERGARTEN'].includes(explicit)) return 'K';
    const d = explicit.replace(/[^0-9]/g, '');
    if (d) return d;
  }
  if (child.birth_date) {
    try {
      const bd = new Date(child.birth_date);
      const now = new Date();
      const m = (now.getFullYear() - bd.getFullYear()) * 12 + (now.getMonth() - bd.getMonth());
      if (m < 60)  return 'PK';
      if (m < 72)  return 'K';
      if (m < 84)  return '1';
      if (m < 96)  return '2';
      if (m < 108) return '3';
      if (m < 120) return '4';
      if (m < 132) return '5';
      if (m < 144) return '6';
      return '7';
    } catch { /* noop */ }
  }
  return 'K';
};

const ExploreLessonPage = () => {
  const { childId, module: mod, conceptKey } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [child, setChild] = useState(null);
  const [phase, setPhase] = useState('teach');   // teach | practice | done
  const [questions, setQuestions] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [loadingQs, setLoadingQs] = useState(false);

  const grade = useMemo(() => gradeFromChild(child), [child]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/children/${childId}`);
        setChild(r.data);
      } catch { toast.error('Could not load child'); }
    })();
  }, [childId, api]);

  const startPractice = async () => {
    if (!child) return;
    setLoadingQs(true);
    setPhase('practice');
    try {
      const r = await api.get(`/learn/practice/${mod}/${conceptKey}`, { params: { grade, child_id: childId } });
      setQuestions(r.data.questions || []);
      setQIdx(0);
      setSelected(null);
      setCorrectCount(0);
    } catch {
      toast.error('Could not load practice questions — please try again.');
      setPhase('teach');
    } finally {
      setLoadingQs(false);
    }
  };

  const currentQ = questions[qIdx];
  const isCorrect = selected !== null && currentQ && selected === currentQ.correct_index;
  const progressPct = questions.length ? Math.round(((qIdx) / questions.length) * 100) : 0;

  const submit = (idx) => {
    if (selected !== null) return;
    setSelected(idx);
    if (idx === currentQ.correct_index) setCorrectCount((c) => c + 1);
  };

  const next = () => {
    setSelected(null);
    if (qIdx + 1 >= questions.length) {
      setPhase('done');
    } else {
      setQIdx((i) => i + 1);
    }
  };

  const restart = () => { setPhase('teach'); setQuestions([]); setQIdx(0); setSelected(null); setCorrectCount(0); };

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/60 via-white to-slate-50 pb-24" data-testid="explore-lesson-page">
      <PracticeExitControls childId={childId} onExit={() => navigate(`/explore/${childId}`)} />

      <div className="max-w-2xl mx-auto px-4 pt-16 sm:pt-8">
        {phase === 'teach' && (
          <>
            <ConceptTeachCard
              module={mod}
              conceptKey={conceptKey}
              ageGroup={grade === 'PK' ? 'preschool' : (['K', '1'].includes(grade) ? 'early_elementary' : 'elementary')}
              storageKey={`zazu.taught.${childId}.${mod}.${conceptKey}`}
              onDone={startPractice}
            />
            <div className="text-center mt-4">
              <Button variant="outline" onClick={startPractice} data-testid="explore-start-practice-btn">
                Skip to practice →
              </Button>
            </div>
          </>
        )}

        {phase === 'practice' && (
          <div data-testid="explore-practice-view">
            <div className="mb-4 flex items-center gap-3">
              <span className="text-sm font-medium text-slate-600">
                {Math.min(qIdx + 1, questions.length)} / {questions.length || '…'}
              </span>
              <Progress value={progressPct} className="flex-1 h-3" />
            </div>

            {loadingQs || !currentQ ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
              </div>
            ) : (
              <Card className="border-2 border-teal-100">
                <CardContent className="p-6">
                  <h2 className="font-fredoka text-2xl font-bold text-slate-900 mb-5 text-center" data-testid="explore-question">
                    {currentQ.question}
                  </h2>
                  <div className="grid grid-cols-2 gap-3">
                    {currentQ.options.map((opt, i) => {
                      const active = selected === i;
                      const showResult = selected !== null;
                      const isRightPick = showResult && i === currentQ.correct_index;
                      return (
                        <motion.button
                          key={i}
                          whileHover={selected === null ? { scale: 1.03 } : {}}
                          whileTap={selected === null ? { scale: 0.97 } : {}}
                          onClick={() => submit(i)}
                          disabled={selected !== null}
                          data-testid={`explore-option-${i}`}
                          className={`p-4 rounded-2xl text-lg font-semibold border-2 transition-all min-h-[64px] ${
                            active
                              ? (isCorrect ? 'border-teal-500 bg-teal-50 text-teal-900' : 'border-red-400 bg-red-50 text-red-900')
                              : isRightPick
                                ? 'border-teal-500 bg-teal-50 text-teal-900'
                                : 'border-slate-200 bg-white hover:border-teal-300 text-slate-800'
                          }`}
                        >
                          {opt}
                        </motion.button>
                      );
                    })}
                  </div>
                  {selected !== null && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-5 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2 text-slate-700">
                        {isCorrect ? <Check className="w-5 h-5 text-teal-600" data-testid="explore-feedback-correct" /> : <X className="w-5 h-5 text-red-500" data-testid="explore-feedback-wrong" />}
                        <span className="text-sm font-semibold">
                          {isCorrect ? 'Nice work!' : `Answer: ${currentQ.options[currentQ.correct_index]}`}
                        </span>
                      </div>
                      <Button onClick={next} className="bg-teal-600 hover:bg-teal-700" data-testid="explore-next-question">
                        {qIdx + 1 >= questions.length ? 'Finish' : 'Next'}
                      </Button>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {phase === 'done' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
            data-testid="explore-done-view"
          >
            <div className="mx-auto w-24 h-24 rounded-full bg-teal-100 flex items-center justify-center mb-4">
              <Trophy className="w-12 h-12 text-teal-600" />
            </div>
            <h2 className="font-fredoka text-3xl font-bold text-slate-900 mb-2">Great job!</h2>
            <p className="text-slate-600 mb-6">
              You got <strong>{correctCount}</strong> out of <strong>{questions.length}</strong> correct.
            </p>
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={restart} data-testid="explore-restart-btn">
                <RotateCcw className="w-4 h-4 mr-2" /> Practice again
              </Button>
              <Button onClick={() => navigate(`/explore/${childId}`)} className="bg-teal-600 hover:bg-teal-700" data-testid="explore-pick-another-btn">
                <Home className="w-4 h-4 mr-2" /> Pick another topic
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ExploreLessonPage;
